import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

interface Complaint {
  id: number;
  type: any;
  payment_reference: string;
  description: string;
  reference: string;
  status: string;
  created_at: string;
  updated_at: string;
  user_id: string;
  user: {
    id: number;
    name: string;
    nom: string;
    prenom: string;
    email: string;
    telephone: string;
    profile_picture: string;
    rang: string;
    _hideImage?: boolean;
  };
  against_type?: string;
  against_id?: string;
  against?: any;
  events?: any[];
}

interface StatusHistory {
  id: number;
  complaint_id: number;
  old_status: string;
  new_status: string;
  comment: string;
  created_by: string;
  created_at: string;
}

@Component({
  selector: 'app-complaint-detail-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './complaint-detail-modal.component.html',
  styleUrls: ['./complaint-detail-modal.component.scss']
})
export class ComplaintDetailModalComponent implements OnInit {
  @Input() complaint!: Complaint;
  statusHistory: StatusHistory[] = [];

  constructor(public activeModal: NgbActiveModal) { }

  ngOnInit() {
    this.loadStatusHistory();
  }

  loadStatusHistory() {
    console.log("complaint", this.complaint);
    if (!this.complaint || !this.complaint.events) return;

    this.statusHistory = this.complaint.events.map(event => ({
      id: event.id,
      complaint_id: event.complaint_id,
      old_status: '',
      new_status: event.status,
      comment: event.message,
      // on doit changer created by par l'admin qui traite cette plainte  !!!!
      created_by: this.complaint?.user?.name || 'Système',
      created_at: event.created_at
    }));

  }

  getStatusDisplay(status: string) {
    switch (status) {
      case "resolved":
      case "résolue":
        return { class: "status-accepted", text: "Résolue" };
      case "in_progress":
        return { class: "status-unknown", text: "En cours de traitement" };
      case "validated":
      case "validée":
        return { class: "status-accepted", text: "Validée" };
      case "rejected":
      case "rejetée":
        return { class: "status-rejected", text: "Rejetée" };
      case "pending":
      case "en_attente":
        return { class: "status-pending", text: "En attente" };
      case "in_analysis":
      case "en_cours_analyse":
        return { class: "status-unknown", text: "En cours d'analyse" };
      case "closed":
      case "fermée":
        return { class: "status-unknown", text: "Fermée" };
      default:
        return { class: "status-pending", text: "En attente" };
    }
  }

  getTreatmentLevel(status: string): number {
    switch (status) {
      case "pending":
      case "en_attente":
        return 1;
      case "validated":
      case "validée":
        return 2;
      case "in_progress":
      case "in_analysis":
      case "en_cours_analyse":
        return 3;
      case "resolved":
      case "résolue":
      case "rejected":
      case "rejetée":
      case "closed":
      case "fermée":
        return 4;
      default:
        return 1;
    }
  }

  getTreatmentLevelLabel(level: number): string {
    switch (level) {
      case 1:
        return "Validation Initiale";
      case 2:
        return "Validé - En attente d'analyse";
      case 3:
        return "En cours d'analyse";
      case 4:
        return "Traitement terminé";
      default:
        return "Non défini";
    }
  }

  getTreatmentLevelColor(level: number): string {
    switch (level) {
      case 1:
        return "#ffc107"; // jaune
      case 2:
        return "#17a2b8"; // bleu
      case 3:
        return "#6f42c1"; // violet
      case 4:
        return "#28a745"; // vert
      default:
        return "#6c757d"; // gris
    }
  }

  getTypeLabel(type: any): string {
    if (!type) return '-';
    const typeKey = typeof type === 'string' ? type : type.name || '';

    const types: { [key: string]: string } = {
      'payment_failed': 'Paiement échoué',
      'payment_pending': 'Paiement en attente',
      'service_quality': 'Qualité du service',
      'other': 'Autre'
    };
    return types[typeKey] || typeKey;
  }

  getTypeBadgeClass(type: any): string {
    if (!type) return 'bg-secondary';
    const typeKey = typeof type === 'string' ? type : type.name || '';

    const classes: { [key: string]: string } = {
      'payment_failed': 'bg-danger',
      'payment_pending': 'bg-warning',
      'service_quality': 'bg-info',
      'other': 'bg-secondary'
    };
    return classes[typeKey] || 'bg-secondary';
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  isValidImage(url: any): boolean {
    if (!url) return false;
    if (typeof url !== 'string') return false;
    const trimmed = url.trim().toLowerCase();
    if (trimmed === '' || trimmed === 'null' || trimmed === 'undefined') return false;
    const defaultPatterns = ['default.png', 'user.png', 'avatar.png', 'default-avatar'];
    if (defaultPatterns.some(pattern => trimmed.includes(pattern))) return false;
    return true;
  }

  onImageError(obj: any) {
    if (obj) {
      obj._hideImage = true;
    }
  }

  getDisplayName(user: any): string {
    if (!user) return 'Inconnu';
    return user.scene_name || (user.prenom + ' ' + user.nom).trim() || user.name || 'Inconnu';
  }

  getAvatarInitial(name: string): string {
    if (!name || name.startsWith('Utilisateur #')) return '?';
    return name.trim().charAt(0).toUpperCase();
  }

  getAvatarColor(name: string): string {
    if (!name || name === 'Yokastar' || name.startsWith('Utilisateur #')) return '#0389C9';
    const colors = [
      '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
      '#2196F3', '#00BCD4', '#009688', '#4CAF50', '#8BC34A',
      '#CDDC39', '#FFC107', '#FF9800'
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }
} 